import type { FeatureMatrixRelationshipPathsQuery } from "src/graphql";
import { EventIcon, ParentIcon, RelatedIcon } from "src/pages/schema/icons";
import { ColumnType } from "src/types/visual";
import { orderThroughPathSegments } from "src/utils/relationship-names";
import {
  FM_COLUMN_TYPE_UNIX_TIMESTAMP,
  FMRelationshipConfigAggType,
  type ConsentPurgeConfig,
  type FMColumnType,
  type ImputationConfig,
} from "@hightouch/lib/customer-data/decision-engine/types";
import {
  DEFAULT_CONSENT_PURGE_MODE,
  type ConsentPurgeMode,
} from "@hightouch/lib/customer-data/decision-engine/consent-purge";
import { getFilterIdAsync } from "@hightouch/lib/customer-data/decision-engine/filter-hash";
import { iterateColumnVariants } from "@hightouch/lib/customer-data/decision-engine/feature-naming";

import type {
  ColumnsResult,
  ConsentPurgeFormValue,
  FeatureMatrixConsentData,
  FMColumnConfig,
  FeatureMatrixData,
  FeatureMatrixFormValues,
  InlineFilter,
  PathSegment,
  RelationshipWithColumns,
  SidebarModel,
  UnusableModel,
} from "./types";

export const OVERALL_COUNT_KEY = "__overall_count__";

export function countInsightsColumns(
  relationships: Record<string, Record<string, FMColumnConfig>>,
): number {
  return Object.values(relationships).reduce(
    (sum, columnMap) =>
      sum +
      Object.entries(columnMap).filter(
        ([name, config]) =>
          name !== OVERALL_COUNT_KEY && config.insightsEnabled,
      ).length,
    0,
  );
}

export interface ComputedVariant {
  /** Backend-compatible unprefixed output name (e.g., "amount_sum_7d", "amount_sum_a3f2c1d4_7d") */
  backendName: string;
  /** Aggregation type for badge display */
  aggregation: string;
  /** Time window in days (-1 = all time) */
  timeWindow: number;
  /** Filter conditions (for rendering filter badge via inferFilterName) */
  filterConditions: object[] | null;
}

/**
 * Computes variant descriptors for a column from form state, including
 * backend-compatible output names. Each variant represents an
 * aggregation × time window × filter combination.
 *
 * Async because filter hashing uses Web Crypto SHA-256.
 */
export async function computeVariantsForColumn(
  columnName: string,
  config: FMColumnConfig,
  requiresAggregation: boolean,
): Promise<ComputedVariant[]> {
  if (!requiresAggregation || config.aggregates.length === 0) {
    return [];
  }

  // Pre-compute filter IDs (async) and build a filterId → conditions lookup.
  // Each InlineFilter has exactly one condition element matching the backend's
  // filters array structure. Hash each condition individually.
  const filters = config.filters ?? [];
  const filterIds: (string | undefined)[] = [];
  const filterConditionsById = new Map<string, object[]>();
  for (const filter of filters) {
    for (const condition of filter.conditions) {
      const filterId = await getFilterIdAsync(condition as object);
      filterIds.push(filterId);
      filterConditionsById.set(filterId, [condition]);
    }
  }
  if (config.includeUnfiltered) {
    filterIds.push(undefined);
  }

  return iterateColumnVariants(
    columnName,
    config.aggregates,
    filterIds,
    config.timeWindowDays,
  ).map((v) => ({
    backendName: v.outputName,
    aggregation: v.aggregationType,
    timeWindow: v.timeWindow,
    filterConditions:
      v.filterId !== undefined
        ? (filterConditionsById.get(v.filterId) ?? null)
        : null,
  }));
}

export const SHOULD_DIRTY_OPTION = { shouldDirty: true } as const;

/**
 * Consent purge decides what customer data Hightouch keeps, so both the consent
 * settings and the per-column Sanitize flags are workspace-admin only.
 */
export const SANITIZE_PERMISSION = {
  v2: { resource: "workspace", grant: "can_update" },
} as const;

export const CAST_TO_OPTIONS: { label: string; value: FMColumnType }[] = [
  { label: "String", value: ColumnType.String },
  { label: "Number", value: ColumnType.Number },
  { label: "Big Integer", value: ColumnType.BigInt },
  { label: "Boolean", value: ColumnType.Boolean },
  { label: "Timestamp", value: ColumnType.Timestamp },
  { label: "Date", value: ColumnType.Date },
  { label: "Unix Timestamp", value: FM_COLUMN_TYPE_UNIX_TIMESTAMP },
];

export const ALL_AGGREGATION_OPTIONS = [
  { label: "count", value: "count" },
  { label: "sum", value: "sum" },
  { label: "avg", value: "average" },
  { label: "min", value: "min" },
  { label: "max", value: "max" },
  { label: "mode", value: "mode" },
  { label: "first", value: "first" },
  { label: "last", value: "last" },
  { label: "count distinct", value: "count_distinct" },
];

const STRING_AGGREGATION_OPTIONS = [
  { label: "count", value: "count" },
  { label: "mode", value: "mode" },
  { label: "first", value: "first" },
  { label: "last", value: "last" },
  { label: "count distinct", value: "count_distinct" },
];

export const TIME_WINDOW_OPTIONS = [
  { label: "All Time", value: -1 },
  { label: "7d", value: 7 },
  { label: "14d", value: 14 },
  { label: "21d", value: 21 },
  { label: "30d", value: 30 },
  { label: "60d", value: 60 },
  { label: "90d", value: 90 },
  { label: "180d", value: 180 },
  { label: "365d", value: 365 },
];

export function getAggregationOptions(
  columnType: string,
  hasTimestamp: boolean,
) {
  const lower = columnType.toLowerCase();
  let options =
    lower === "string" || lower === "text" || lower === "varchar"
      ? STRING_AGGREGATION_OPTIONS
      : ALL_AGGREGATION_OPTIONS;

  if (!hasTimestamp) {
    options = options.filter((o) => o.value !== "first" && o.value !== "last");
  }
  return options;
}

const ALWAYS_NUMERIC_AGGREGATIONS = new Set<string>([
  FMRelationshipConfigAggType.COUNT,
  FMRelationshipConfigAggType.COUNT_DISTINCT,
  FMRelationshipConfigAggType.SUM,
  FMRelationshipConfigAggType.AVERAGE,
]);

const NUMERIC_COLUMN_TYPES = new Set<string>([
  ColumnType.Number,
  ColumnType.BigInt,
]);

export function isImputationEligible(
  colType: string,
  aggregates: string[],
  requiresAggregation: boolean,
  castTo?: string | null,
): boolean {
  const effectiveType = castTo ?? colType;
  const isNumericType = NUMERIC_COLUMN_TYPES.has(effectiveType.toLowerCase());

  if (!requiresAggregation) {
    return isNumericType;
  }

  if (aggregates.length === 0) {
    return false;
  }

  if (isNumericType) {
    return aggregates.length > 0;
  }
  return aggregates.some((agg) => ALWAYS_NUMERIC_AGGREGATIONS.has(agg));
}

export function getModelTypeIcon(type: "parent" | "event" | "related") {
  if (type === "parent") return ParentIcon;
  if (type === "event") return EventIcon;
  return RelatedIcon;
}

/**
 * A relationship is "truly merged" when its columns are surfaced under
 * another relationship (typically the parent model). These relationships
 * should not be treated as independent entries — their columns are managed
 * through the merged-into relationship.
 */
export function isTrulyMergedRelationship(
  rel: RelationshipWithColumns,
  allRelationships: RelationshipWithColumns[],
): boolean {
  return rel.mergedIntoRelationshipIds.some((locId) => {
    if (locId === "parent") return true;
    const otherRel = allRelationships.find(
      (r) => String(r.relationshipId) === locId,
    );
    return !otherRel || otherRel.targetModelId !== rel.targetModelId;
  });
}

/**
 * Relationships hidden from the sidebar because their columns are merged
 * into another location. Only visible locations are named: merges are
 * transitive, so columns behind a hidden sibling also surface somewhere
 * visible.
 */
export function buildUnusableModels(
  columnsResult: ColumnsResult,
): UnusableModel[] {
  const visibleRelIds = new Set(
    columnsResult.relationships
      .filter((r) => !isTrulyMergedRelationship(r, columnsResult.relationships))
      .map((r) => r.relationshipId),
  );

  const models: UnusableModel[] = [];
  for (const rel of columnsResult.relationships) {
    if (!isTrulyMergedRelationship(rel, columnsResult.relationships)) continue;

    const mergedIntoNames = new Set<string>();
    for (const locId of rel.mergedIntoRelationshipIds) {
      if (locId === "parent") {
        mergedIntoNames.add(columnsResult.parentModel.modelName);
        continue;
      }
      if (!visibleRelIds.has(locId)) continue;
      const other = columnsResult.relationships.find(
        (r) => r.relationshipId === locId,
      );
      if (other && other.targetModelId !== rel.targetModelId) {
        mergedIntoNames.add(other.targetModelName);
      }
    }

    models.push({
      relationshipId: rel.relationshipId,
      name: rel.targetModelName,
      type: rel.isEventModel ? "event" : "related",
      mergedIntoNames: [...mergedIntoNames],
    });
  }
  return models.sort((a, b) => a.name.localeCompare(b.name));
}

interface SavedRelationshipConfig {
  columns?: {
    name: string;
    modelId: string;
    aggregations?: string[];
    filters?: unknown[];
    includeUnfiltered?: boolean;
    timeWindowDays?: number[];
    dependency?: boolean;
    castTo?: string;
    imputation?: ImputationConfig;
    insightsEnabled?: boolean;
    insightsExcludedVariants?: string[];
    sanitizeOnConsentRevocation?: boolean;
  }[];
  timeWindowDays?: number[];
  timestampColumn?: string;
  isLegacyFeatureMatrixModel?: boolean;
}

export function buildInitialValues(
  fm: Pick<FeatureMatrixData, "name"> & {
    relationships: Pick<
      FeatureMatrixData["relationships"][number],
      "relationship_id" | "config" | "enabled"
    >[];
  },
  columnsResult: ColumnsResult,
  matrixConfig: FeatureMatrixConsentData["config"] | null,
): FeatureMatrixFormValues {
  const relationships: Record<string, Record<string, FMColumnConfig>> = {};
  const enabledRelationships: Record<string, boolean> = {};
  const timestampColumnOverrides: Record<string, string> = {};
  const legacyRelationships: Record<string, boolean> = {};

  for (const savedRel of fm.relationships) {
    const key =
      savedRel.relationship_id === null
        ? "parent"
        : String(savedRel.relationship_id);
    const config = savedRel.config as SavedRelationshipConfig | null;
    enabledRelationships[key] = savedRel.enabled;
    legacyRelationships[key] = Boolean(config?.isLegacyFeatureMatrixModel);

    if (config) {
      relationships[key] = {};

      const relRequiresAgg =
        key !== "parent" &&
        columnsResult.relationships.some(
          (r) => String(r.relationshipId) === key && r.requiresAggregation,
        );
      if (relRequiresAgg) {
        relationships[key][OVERALL_COUNT_KEY] = {
          aggregates: [],
          filters: [],
          includeUnfiltered: false,
          timeWindowDays: config.timeWindowDays ?? [],
          dependency: false,
          castTo: null,
          imputation: null,
          insightsEnabled: false,
          insightsExcludedVariants: [],
          sanitizeOnConsentRevocation: false,
        };
      }

      for (const col of config.columns ?? []) {
        const inlineFilters: InlineFilter[] = (
          (col.filters ?? []) as object[]
        ).map((f) => ({
          id: crypto.randomUUID(),
          conditions: [f],
        }));

        relationships[key][col.name] = {
          aggregates: col.aggregations ?? [],
          filters: inlineFilters,
          includeUnfiltered: col.includeUnfiltered ?? false,
          timeWindowDays: col.timeWindowDays ?? null,
          dependency: col.dependency ?? false,
          castTo: col.castTo ?? null,
          imputation: col.imputation ?? null,
          insightsEnabled: col.insightsEnabled ?? false,
          insightsExcludedVariants: col.insightsExcludedVariants ?? [],
          sanitizeOnConsentRevocation: col.sanitizeOnConsentRevocation ?? false,
        };
      }
    }

    if (config?.timestampColumn) {
      const rel = columnsResult.relationships.find(
        (r) => String(r.relationshipId) === key,
      );
      if (rel && rel.timestampColumn !== config.timestampColumn) {
        timestampColumnOverrides[key] = config.timestampColumn;
      }
    }
  }

  // Selections under a truly-merged relationship stay put. Feature names
  // embed the relationship id, so moving them would rename features and lose
  // the agent's learnings. Empty entries are dropped for cleanup on save.
  for (const rel of columnsResult.relationships) {
    if (!isTrulyMergedRelationship(rel, columnsResult.relationships)) continue;
    const key = String(rel.relationshipId);
    const hasFeatures = Object.entries(relationships[key] ?? {}).some(
      ([name, config]) =>
        name !== OVERALL_COUNT_KEY || Boolean(config.timeWindowDays?.length),
    );
    if (!hasFeatures) {
      delete relationships[key];
      delete enabledRelationships[key];
    }
  }

  if (!relationships.parent) {
    relationships.parent = {};
  }
  if (enabledRelationships.parent === undefined) {
    enabledRelationships.parent = true;
  }

  return {
    name: fm.name,
    enabledRelationships,
    relationships,
    timestampColumnOverrides,
    legacyRelationships,
    consentPurge: readConsentPurge(matrixConfig),
  };
}

export function readConsentPurge(
  config: FeatureMatrixConsentData["config"] | null,
): ConsentPurgeFormValue | null {
  const consentPurge = (config as { consentPurge?: ConsentPurgeConfig } | null)
    ?.consentPurge;
  if (!consentPurge?.revocationsModelId) {
    return null;
  }
  return {
    revocationsModelId: consentPurge.revocationsModelId,
    primaryKey: consentPurge.columns?.primaryKey ?? "",
    revokedAt: consentPurge.columns?.revokedAt ?? "",
    mode: consentPurge.mode ?? DEFAULT_CONSENT_PURGE_MODE,
  };
}

export function consentPurgeForModel(
  current: ConsentPurgeFormValue | null,
  revocationsModelId: string | undefined,
  savedMode: ConsentPurgeMode,
): ConsentPurgeFormValue | null {
  if (!revocationsModelId) {
    return null;
  }
  return {
    revocationsModelId,
    primaryKey: "",
    revokedAt: "",
    mode: current?.mode ?? savedMode,
  };
}

/**
 * Shared by the pre-flight validation and the save so the two cannot disagree.
 * The mode is always stated: the resolver reads an omitted one as "keep the
 * stored mode", which is how the form and the stored config drift apart.
 */
export function toConsentPurgeInput(
  consentPurge: ConsentPurgeFormValue | null,
): ConsentPurgeConfig | null {
  if (!consentPurge) {
    return null;
  }
  return {
    revocationsModelId: consentPurge.revocationsModelId,
    columns: {
      primaryKey: consentPurge.primaryKey,
      revokedAt: consentPurge.revokedAt,
    },
    mode: consentPurge.mode,
  };
}

export type RemovedMergedColumn = {
  relationshipKey: string;
  modelName: string;
  columnName: string;
};

/**
 * Columns removed from a truly-merged relationship's saved config. Removal is
 * permanent because reselecting elsewhere creates a differently named feature
 * without the old learnings. A windowless overall count generates no features
 * and is not reported.
 */
export function findRemovedMergedColumns(
  initial: FeatureMatrixFormValues["relationships"],
  current: FeatureMatrixFormValues["relationships"],
  columnsResult: ColumnsResult,
): RemovedMergedColumn[] {
  const removed: RemovedMergedColumn[] = [];
  for (const rel of columnsResult.relationships) {
    if (!isTrulyMergedRelationship(rel, columnsResult.relationships)) continue;
    const key = String(rel.relationshipId);
    const currentCols = current[key] ?? {};
    for (const [name, config] of Object.entries(initial[key] ?? {})) {
      if (Object.hasOwn(currentCols, name)) continue;
      if (name === OVERALL_COUNT_KEY && !config.timeWindowDays?.length) {
        continue;
      }
      removed.push({
        relationshipKey: key,
        modelName: rel.targetModelName,
        columnName: name,
      });
    }
  }
  return removed;
}

export type MissingAggregationColumn = {
  relationshipKey: string;
  modelName: string;
  columnName: string;
};

export function findColumnsMissingAggregations(
  relationships: FeatureMatrixFormValues["relationships"],
  columnsResult: ColumnsResult,
): MissingAggregationColumn[] {
  const missing: MissingAggregationColumn[] = [];
  for (const [key, columnMap] of Object.entries(relationships)) {
    const rel = columnsResult.relationships.find(
      (r) => String(r.relationshipId) === key,
    );
    if (!rel?.requiresAggregation) continue;

    for (const [columnName, config] of Object.entries(columnMap)) {
      if (columnName === OVERALL_COUNT_KEY) continue;
      if (config.aggregates.length === 0) {
        missing.push({
          relationshipKey: key,
          modelName: rel.targetModelName,
          columnName,
        });
      }
    }
  }
  return missing;
}

export type HiddenAggregationColumn = MissingAggregationColumn &
  Pick<FMColumnConfig, "aggregates">;

/**
 * Aggregations saved while a relationship was 1:many, which the 1:1 table hides.
 * The backend still aggregates the relationship and drops its unaggregated columns.
 */
export function findHiddenAggregationColumns(
  relationships: FeatureMatrixFormValues["relationships"],
  columnsResult: ColumnsResult,
): HiddenAggregationColumn[] {
  const hidden: HiddenAggregationColumn[] = [];
  for (const rel of columnsResult.relationships) {
    if (
      rel.requiresAggregation ||
      isTrulyMergedRelationship(rel, columnsResult.relationships)
    ) {
      continue;
    }
    const key = String(rel.relationshipId);
    for (const [columnName, config] of Object.entries(
      relationships[key] ?? {},
    )) {
      if (columnName === OVERALL_COUNT_KEY) continue;
      if (config.aggregates.length > 0) {
        hidden.push({
          relationshipKey: key,
          modelName: rel.targetModelName,
          columnName,
          aggregates: config.aggregates,
        });
      }
    }
  }
  return hidden;
}

export function clearAggregations(
  columnMap: Record<string, FMColumnConfig>,
): Record<string, FMColumnConfig> {
  return Object.fromEntries(
    Object.entries(columnMap).map(([name, config]) => [
      name,
      {
        ...config,
        aggregates: [],
        filters: [],
        includeUnfiltered: false,
        timeWindowDays: [],
        insightsExcludedVariants: [],
      },
    ]),
  );
}

/** Removing a hidden aggregation renames the column's features. */
export function findClearedHiddenAggregations(
  initial: FeatureMatrixFormValues["relationships"],
  current: FeatureMatrixFormValues["relationships"],
  columnsResult: ColumnsResult,
): HiddenAggregationColumn[] {
  return findHiddenAggregationColumns(initial, columnsResult).filter(
    (column) =>
      current[column.relationshipKey]?.[column.columnName]?.aggregates
        .length === 0,
  );
}

export type FeatureMatrixRelationshipUpsert = {
  decision_engine_feature_matrix_id: string;
  relationship_id: number | null;
  config: Record<string, unknown>;
  enabled: boolean;
};

export async function buildRelationshipUpserts({
  featureMatrixId,
  data,
  columnsResult,
  savedRelationships,
}: {
  featureMatrixId: string;
  data: FeatureMatrixFormValues;
  columnsResult: ColumnsResult;
  savedRelationships: Pick<
    FeatureMatrixData["relationships"][number],
    "id" | "relationship_id"
  >[];
}): Promise<{
  relationshipsToUpsert: FeatureMatrixRelationshipUpsert[];
  idsToDelete: string[];
}> {
  const savedRelIds = new Map<string, string>();
  for (const rel of savedRelationships) {
    const key =
      rel.relationship_id === null ? "parent" : String(rel.relationship_id);
    savedRelIds.set(key, rel.id);
  }

  const relationshipsToUpsert: FeatureMatrixRelationshipUpsert[] = [];
  const idsToDelete: string[] = [];

  for (const [key, enabled] of Object.entries(data.enabledRelationships)) {
    const columnMap = data.relationships[key] ?? {};
    const overallCount = columnMap[OVERALL_COUNT_KEY];
    const realColumnEntries = Object.entries(columnMap).filter(
      ([name]) => name !== OVERALL_COUNT_KEY,
    );
    const hasColumns =
      realColumnEntries.length > 0 || overallCount !== undefined;

    if (!hasColumns) {
      const existingId = savedRelIds.get(key);
      if (existingId) {
        idsToDelete.push(existingId);
      }
      continue;
    }

    const rel = columnsResult.relationships.find(
      (r) => String(r.relationshipId) === key,
    );
    const requiresAgg = rel?.requiresAggregation ?? false;

    const columns = await Promise.all(
      realColumnEntries.map(async ([colName, config]) => {
        let excludedVariants = config.insightsExcludedVariants ?? [];
        if (excludedVariants.length > 0) {
          const validVariants = await computeVariantsForColumn(
            colName,
            config,
            requiresAgg,
          );
          const validNames = new Set(validVariants.map((v) => v.backendName));
          excludedVariants = excludedVariants.filter((v) => validNames.has(v));
        }

        const filters = (config.filters ?? []).flatMap((f) => f.conditions);

        return {
          name: colName,
          modelId: resolveModelId(key, colName, columnsResult),
          aggregations: config.aggregates,
          filters,
          ...(config.includeUnfiltered && filters.length > 0
            ? { includeUnfiltered: true }
            : {}),
          ...(config.timeWindowDays !== null
            ? { timeWindowDays: config.timeWindowDays }
            : {}),
          ...(config.dependency ? { dependency: true } : {}),
          ...(config.castTo ? { castTo: config.castTo } : {}),
          ...(config.imputation ? { imputation: config.imputation } : {}),
          ...(config.insightsEnabled ? { insightsEnabled: true } : {}),
          ...(excludedVariants.length
            ? { insightsExcludedVariants: excludedVariants }
            : {}),
          ...(config.sanitizeOnConsentRevocation
            ? { sanitizeOnConsentRevocation: true }
            : {}),
        };
      }),
    );

    const timestampColumn =
      data.timestampColumnOverrides[key] ?? rel?.timestampColumn ?? undefined;

    const relationshipTimeWindowDays =
      overallCount?.timeWindowDays ?? undefined;

    relationshipsToUpsert.push({
      decision_engine_feature_matrix_id: featureMatrixId,
      relationship_id: key === "parent" ? null : Number(key),
      config: {
        columns,
        ...(timestampColumn ? { timestampColumn } : {}),
        ...(relationshipTimeWindowDays !== undefined &&
        relationshipTimeWindowDays !== null
          ? { timeWindowDays: relationshipTimeWindowDays }
          : {}),
        ...(data.legacyRelationships[key]
          ? { isLegacyFeatureMatrixModel: true }
          : {}),
      },
      enabled,
    });
  }

  // Rows absent from form state were dropped by buildInitialValues as merged
  // entries with no selections.
  for (const [key, id] of savedRelIds) {
    if (!Object.hasOwn(data.enabledRelationships, key)) {
      idsToDelete.push(id);
    }
  }

  return { relationshipsToUpsert, idsToDelete };
}

export function resolveModelId(
  relationshipKey: string,
  columnName: string,
  columnsResult: ColumnsResult,
): string {
  if (relationshipKey === "parent") {
    const col = columnsResult.parentModel.columns.find(
      (c) => c.name === columnName,
    );
    return String(col?.model_id ?? columnsResult.parentModel.modelId);
  }
  const rel = columnsResult.relationships.find(
    (r) => String(r.relationshipId) === relationshipKey,
  );
  if (rel) {
    const col = rel.columns.find((c) => c.name === columnName);
    return String(col?.model_id ?? rel.targetModelId);
  }
  return "";
}

export function buildSidebarModels(
  columnsResult: ColumnsResult,
  formValues: Pick<
    FeatureMatrixFormValues,
    "relationships" | "enabledRelationships"
  >,
): SidebarModel[] {
  const models: SidebarModel[] = [];

  const parentCols = formValues.relationships.parent ?? {};
  models.push({
    key: "parent",
    name: columnsResult.parentModel.modelName,
    label: "Parent",
    type: "parent",
    requiresAggregation: false,
    columnCount: Object.keys(parentCols).length,
    relationshipId: null,
    hasHiddenAggregations: false,
  });

  const hiddenAggregationKeys = new Set(
    findHiddenAggregationColumns(formValues.relationships, columnsResult).map(
      (column) => column.relationshipKey,
    ),
  );

  for (const rel of columnsResult.relationships) {
    const key = String(rel.relationshipId);
    const relCols = formValues.relationships[key] ?? {};
    const isMerged = isTrulyMergedRelationship(
      rel,
      columnsResult.relationships,
    );

    // Merged relationships are shown only while saved selections remain.
    if (isMerged && Object.keys(relCols).length === 0) continue;

    let label: string;
    let type: "event" | "related";
    if (rel.isEventModel) {
      label = "Event";
      type = "event";
    } else if (rel.requiresAggregation) {
      label = "Related (1:many)";
      type = "related";
    } else {
      label = "Related (1:1)";
      type = "related";
    }

    models.push({
      key,
      name: rel.targetModelName,
      label: isMerged ? "Merged" : label,
      type,
      requiresAggregation: rel.requiresAggregation,
      columnCount: Object.keys(relCols).length,
      relationshipId: rel.relationshipId,
      hasHiddenAggregations: hiddenAggregationKeys.has(key),
    });
  }

  const typeOrder: Record<string, number> = {
    "Related (1:1)": 0,
    "Related (1:many)": 1,
    Event: 2,
  };
  models.sort((a, b) => {
    if (a.type === "parent") return -1;
    if (b.type === "parent") return 1;
    const orderA = typeOrder[a.label] ?? 3;
    const orderB = typeOrder[b.label] ?? 3;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });

  return models;
}

export function getColumnsForModel(
  modelKey: string,
  columnsResult: ColumnsResult,
) {
  if (modelKey === "parent") {
    return columnsResult.parentModel.columns;
  }
  const rel = columnsResult.relationships.find(
    (r) => String(r.relationshipId) === modelKey,
  );
  return rel?.columns ?? [];
}

export function getRelationshipForModel(
  modelKey: string,
  columnsResult: ColumnsResult,
) {
  if (modelKey === "parent") return null;
  return (
    columnsResult.relationships.find(
      (r) => String(r.relationshipId) === modelKey,
    ) ?? null
  );
}

/**
 * Reconstruct an ordered path from unordered through-relationship segments.
 * Segments form a chain: parent -> A -> B -> target.
 * Returns model names along the path (e.g., ["Orders", "LineItems", "Products"]).
 */
export function reconstructPath(
  parentModelId: string,
  segments: PathSegment[],
): string[] {
  return orderThroughPathSegments(parentModelId, segments).map(
    (segment) => segment.to_model.name,
  );
}

/**
 * Disambiguate sidebar models that share the same display name.
 * Uses through-relationship path data to find the first divergent
 * intermediary model and appends " (via X)" to the name.
 */
export function disambiguateSidebarModels(
  models: SidebarModel[],
  pathsData: FeatureMatrixRelationshipPathsQuery | undefined,
  parentModelId: string,
): SidebarModel[] {
  if (!pathsData) return models;

  const groups = new Map<string, SidebarModel[]>();
  for (const model of models) {
    if (model.type === "parent") continue;
    const list = groups.get(model.name) ?? [];
    list.push(model);
    groups.set(model.name, list);
  }

  const pathsByRelId = new Map<string, PathSegment[]>();
  for (const mr of pathsData.model_relationships) {
    pathsByRelId.set(
      String(mr.id),
      mr.through_relationships.map((tr) => tr.relationship),
    );
  }

  for (const [, group] of groups) {
    if (group.length < 2) continue;

    const pathInfos = group.map((model) => {
      const segments = pathsByRelId.get(model.key) ?? [];
      const fullPath = reconstructPath(parentModelId, segments);
      const intermediaries = fullPath.slice(0, -1);
      return { model, intermediaries, fullPath };
    });

    const maxLen = Math.max(...pathInfos.map((p) => p.intermediaries.length));
    let divergentIdx = -1;
    for (let i = 0; i < maxLen; i++) {
      const values = pathInfos.map((p) => p.intermediaries[i] ?? null);
      const unique = new Set(values.filter(Boolean));
      if (unique.size > 1 || values.some((v) => v === null)) {
        divergentIdx = i;
        break;
      }
    }

    for (const info of pathInfos) {
      if (info.intermediaries.length === 0) {
        info.model = { ...info.model, name: `${info.model.name} (direct)` };
      } else if (divergentIdx >= 0 && info.intermediaries[divergentIdx]) {
        info.model = {
          ...info.model,
          name: `${info.model.name} (via ${info.intermediaries[divergentIdx]})`,
        };
      } else {
        const last = info.intermediaries[info.intermediaries.length - 1];
        if (last) {
          info.model = {
            ...info.model,
            name: `${info.model.name} (via ${last})`,
          };
        }
      }
    }
  }

  return models;
}
